/**
 * CryptoService
 *
 * Browser-side at-rest encryption for sensitive IndexedDB data.
 *
 * Architecture:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  device_secret (random 32 bytes, stored in params table)    │
 * │       │                                                     │
 * │       ▼  HKDF-SHA256 (info = "mycrogym-storage-v1")       │
 * │  AES-256-GCM CryptoKey  (non-extractable, in-memory only) │
 * │       │                                                     │
 * │       ├─► encrypt(user.at, user.rt)   → EncryptedBlob     │
 * │       └─► encrypt(integration.config) → EncryptedBlob     │
 * └─────────────────────────────────────────────────────────────┘
 *
 * The device_secret is the root — stored plaintext in IndexedDB params table
 * (analogous to a keychain entry). It is generated once per device on first
 * login and never changes. All encrypted blobs become unreadable without it.
 *
 * Usage:
 *   await CryptoService.ensureReady()        // call before first DB read after boot
 *   const blob = await CryptoService.encrypt({ at: 'eyJ...' })
 *   const plain = await CryptoService.decrypt(blob)
 */

const HKDF_INFO = new TextEncoder().encode('mycrogym-storage-v1')
const HKDF_SALT_STR = 'mycrogym-aes-salt-2024'

/** Shape stored in IndexedDB for encrypted values */
export interface EncryptedBlob {
  v: 1                // version sentinel — allows future format changes
  iv: string          // base64-encoded 96-bit IV
  data: string        // base64-encoded ciphertext + GCM auth tag
}

/** Checks whether a value is an EncryptedBlob (for migration detection) */
export function isEncryptedBlob(value: unknown): value is EncryptedBlob {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as any).v === 1 &&
    typeof (value as any).iv === 'string' &&
    typeof (value as any).data === 'string'
  )
}

// ── Module-level state ─────────────────────────────────────────────────────────

let _aesKey: CryptoKey | null = null
let _initPromise: Promise<void> | null = null

// ── Internal helpers ───────────────────────────────────────────────────────────

function base64ToBytes(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0))
}

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
}

async function deriveKey(deviceSecret: Uint8Array): Promise<CryptoKey> {
  // Import raw secret as HKDF key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    deviceSecret,
    { name: 'HKDF' },
    false,
    ['deriveKey']
  )

  // Derive a non-extractable AES-256-GCM key
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(HKDF_SALT_STR),
      info: HKDF_INFO,
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,         // non-extractable — key never leaves SubtleCrypto
    ['encrypt', 'decrypt']
  )
}

// ── Public API ─────────────────────────────────────────────────────────────────

const CryptoService = {
  /**
   * Returns true if the AES key has been derived and is ready for use.
   */
  isReady(): boolean {
    return _aesKey !== null
  },

  /**
   * Initialize from a base64-encoded device secret string.
   * Idempotent — calling multiple times is safe (returns cached promise).
   *
   * @param deviceSecretB64 - base64 string from params['device_secret']
   */
  async init(deviceSecretB64: string): Promise<void> {
    if (_aesKey) return // Already initialized

    if (_initPromise) return _initPromise

    _initPromise = (async () => {
      const secretBytes = base64ToBytes(deviceSecretB64)
      _aesKey = await deriveKey(secretBytes)
    })()

    return _initPromise
  },

  /**
   * Generate a new random device secret (32 bytes → base64).
   * Called once on first login; result stored in params table.
   */
  generateDeviceSecret(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(32))
    return bytesToBase64(bytes)
  },

  /**
   * Ensure the service is ready. Loads the device secret from IndexedDB params
   * and derives the AES key. Safe to call multiple times (idempotent).
   *
   * Must be called at app boot before any encrypted DB read.
   * Resolves silently if no device secret exists yet (pre-login state).
   */
  async ensureReady(): Promise<void> {
    if (_aesKey) return

    // Lazy import to avoid circular dependency
    const { default: DBConfigService } = await import('./DBConfigService')
    const secret = await DBConfigService.getParam('device_secret')
    if (!secret) return // Pre-login — no key yet, that's fine

    await this.init(secret)
  },

  /**
   * Encrypt any JSON-serializable value.
   * Returns an EncryptedBlob that can be stored in IndexedDB.
   *
   * @throws if CryptoService has not been initialized
   */
  async encrypt(plaintext: unknown): Promise<EncryptedBlob> {
    if (!_aesKey) throw new Error('[CryptoService] Not initialized — call ensureReady() first')

    const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV
    const encoded = new TextEncoder().encode(JSON.stringify(plaintext))

    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      _aesKey,
      encoded
    )

    return {
      v: 1,
      iv: bytesToBase64(iv),
      data: bytesToBase64(new Uint8Array(ciphertext)),
    }
  },

  /**
   * Decrypt an EncryptedBlob back to the original value.
   *
   * @throws if CryptoService has not been initialized
   * @throws if blob is malformed or decryption fails (wrong key / tampered data)
   */
  async decrypt<T = unknown>(blob: EncryptedBlob): Promise<T> {
    if (!_aesKey) throw new Error('[CryptoService] Not initialized — call ensureReady() first')

    const iv = base64ToBytes(blob.iv)
    const ciphertext = base64ToBytes(blob.data)

    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      _aesKey,
      ciphertext
    )

    const json = new TextDecoder().decode(plainBuffer)
    return JSON.parse(json) as T
  },

  /**
   * Clear the in-memory key. Call on logout to prevent key reuse.
   */
  destroy(): void {
    _aesKey = null
    _initPromise = null
  },
}

export default CryptoService
